import { useState, useMemo } from 'react'
import { X, Check, LinkIcon } from 'lucide-react'
import { Combobox } from '@/components/ui/Combobox'
import { getTodayForInput } from '../utils/dateHelpers'
import { inputClasses, useTaskTemplateOptions } from '../utils/taskHelpers'
import { usePresalesDashboardStore } from '../store'
import type { PresalesTask, PresalesTaskTemplate } from '@/api/types'
import type { ComboboxOption } from '@/components/ui/Combobox'

interface TaskEditFormProps {
  task: PresalesTask
  teamMemberOptions: ComboboxOption[]
  keyPersonOptions: ComboboxOption[]
  onSave: (taskId: number, data: {
    description: string
    status_update: string | null
    assigned_to: string | null
    due_date: string | null
    jira_ticket: string | null
    key_person: string | null
    deal_id?: number | null
    prospect_company?: string | null
    template?: string
    is_hard_deadline?: boolean
    is_critical_path?: boolean
  }) => Promise<void>
  onCancel: () => void
}

export function TaskEditForm({
  task,
  teamMemberOptions,
  keyPersonOptions,
  onSave,
  onCancel,
}: TaskEditFormProps) {
  const { deals } = usePresalesDashboardStore()

  const [editDescription, setEditDescription] = useState(task.description)
  const [editStatusUpdate, setEditStatusUpdate] = useState(task.status_update || '')
  const [editAssignedTo, setEditAssignedTo] = useState(task.assigned_to || '')
  const [editDueDate, setEditDueDate] = useState(task.due_date || '')
  const [editJiraTicket, setEditJiraTicket] = useState(task.jira_ticket || '')
  const [editKeyPerson, setEditKeyPerson] = useState(task.key_person || '')
  const [editDealId, setEditDealId] = useState<number | ''>(task.deal_id ?? '')
  const [editProspectCompany, setEditProspectCompany] = useState(task.prospect_company || '')
  const [editTemplate, setEditTemplate] = useState<PresalesTaskTemplate>(task.template)
  const [editHardDeadline, setEditHardDeadline] = useState(!!task.is_hard_deadline)
  const [editCriticalPath, setEditCriticalPath] = useState(!!task.is_critical_path)
  const [isSaving, setIsSaving] = useState(false)

  const taskTemplateOptions = useTaskTemplateOptions()

  // Build deal options from store
  const dealOptions = useMemo(() => {
    const openDeals = deals.filter((d) => d.deal_stage !== 'closedwon' && d.deal_stage !== 'closedlost')
    return openDeals.map((d) => ({
      value: d.id.toString(),
      label: d.deal_name + (d.company_name ? ` (${d.company_name})` : ''),
    }))
  }, [deals])

  const handleSave = async () => {
    if (!editDescription.trim()) return
    setIsSaving(true)
    try {
      const saveData: Parameters<typeof onSave>[1] = {
        description: editDescription.trim(),
        status_update: editStatusUpdate.trim() || null,
        assigned_to: editAssignedTo || null,
        due_date: editDueDate || null,
        jira_ticket: editJiraTicket.trim() || null,
        key_person: editKeyPerson.trim() || null,
      }

      // Include template change if different
      if (editTemplate !== task.template) {
        saveData.template = editTemplate
      }

      // Include flag changes
      if (editHardDeadline !== !!task.is_hard_deadline) {
        saveData.is_hard_deadline = editHardDeadline
      }
      if (editCriticalPath !== !!task.is_critical_path) {
        saveData.is_critical_path = editCriticalPath
      }

      // Include deal_id change if it's different from original
      const newDealId = editDealId === '' ? null : editDealId
      const originalDealId = task.deal_id ?? null
      if (newDealId !== originalDealId) {
        saveData.deal_id = newDealId
        // Clear prospect_company when assigning a deal
        if (newDealId) {
          saveData.prospect_company = null
        }
      }

      // Include prospect_company if no deal and it changed
      if (!newDealId && editProspectCompany.trim() !== (task.prospect_company || '')) {
        saveData.prospect_company = editProspectCompany.trim() || null
      }

      await onSave(task.id, saveData)
    } catch (error) {
      console.error('Error saving task edit:', error)
    } finally {
      setIsSaving(false)
    }
  }

  const currentDealName = task.deal_name || (task.deal_id ? `Deal #${task.deal_id}` : null)

  return (
    <div className="px-3 py-3 rounded-lg border border-primary/30 bg-[var(--bg-secondary)] space-y-3">
      {/* Header with template selector */}
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-[var(--text-muted)] uppercase tracking-wide">Edit Task</span>
        <div className="w-[180px]">
          <Combobox
            options={taskTemplateOptions}
            value={editTemplate}
            onChange={(val) => setEditTemplate(val as PresalesTaskTemplate)}
            placeholder="Task type..."
          />
        </div>
        {currentDealName && (
          <span className="text-[10px] text-[var(--text-muted)] flex items-center gap-1">
            <LinkIcon className="h-3 w-3" />
            {currentDealName}
          </span>
        )}
        {!task.deal_id && task.prospect_company && (
          <span className="text-[10px] text-amber-400 flex items-center gap-1">
            Prospect: {task.prospect_company}
          </span>
        )}
      </div>

      {/* Deal / Prospect row — shown for all tasks so deal can be assigned/changed */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">
            Deal {!task.deal_id && <span className="text-amber-400">(not yet assigned)</span>}
          </label>
          <Combobox
            options={dealOptions}
            value={editDealId ? editDealId.toString() : ''}
            onChange={(val) => {
              setEditDealId(val ? parseInt(val) : '')
              if (val) setEditProspectCompany('')
            }}
            placeholder="Search deals to link..."
            allowFreeText={false}
          />
        </div>
        {!editDealId && (
          <div>
            <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Prospect Company</label>
            <input
              type="text"
              className={inputClasses}
              value={editProspectCompany}
              onChange={(e) => setEditProspectCompany(e.target.value)}
              placeholder="Company name (if no deal yet)..."
            />
          </div>
        )}
      </div>

      {/* Description + Status Update */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Description</label>
          <input
            type="text"
            className={inputClasses}
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Task description..."
          />
        </div>
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Status Update</label>
          <input
            type="text"
            className={inputClasses}
            value={editStatusUpdate}
            onChange={(e) => setEditStatusUpdate(e.target.value)}
            placeholder="e.g., Waiting on client..."
          />
        </div>
      </div>

      {/* Fields row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Assigned To</label>
          <Combobox
            options={teamMemberOptions}
            value={editAssignedTo}
            onChange={(val) => setEditAssignedTo(val)}
            placeholder="Assignee..."
            allowFreeText
          />
        </div>
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Due Date</label>
          <div className="flex gap-1">
            <input
              type="date"
              className={inputClasses}
              value={editDueDate}
              onChange={(e) => setEditDueDate(e.target.value)}
            />
            <button
              type="button"
              onClick={() => setEditDueDate(getTodayForInput())}
              className="px-1.5 py-1 text-[10px] bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded hover:bg-[var(--bg-secondary)] text-[var(--text-muted)] whitespace-nowrap flex-shrink-0"
            >
              Today
            </button>
          </div>
        </div>
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Jira Ticket</label>
          <input
            type="text"
            className={inputClasses}
            value={editJiraTicket}
            onChange={(e) => setEditJiraTicket(e.target.value)}
            placeholder="PROJ-123..."
          />
        </div>
        <div>
          <label className="block text-[10px] text-[var(--text-muted)] mb-0.5">Key Person</label>
          <Combobox
            options={keyPersonOptions}
            value={editKeyPerson}
            onChange={(val) => setEditKeyPerson(val)}
            placeholder="Key person..."
            allowFreeText
          />
        </div>
      </div>

      {/* Task flags */}
      <div className="flex items-center gap-4">
        <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={editHardDeadline}
            onChange={(e) => setEditHardDeadline(e.target.checked)}
            className="rounded border-[var(--border-color)] bg-[var(--bg-primary)]"
          />
          <span>🚨 Hard Deadline</span>
        </label>
        <label className="flex items-center gap-1.5 text-xs text-[var(--text-secondary)] cursor-pointer">
          <input
            type="checkbox"
            checked={editCriticalPath}
            onChange={(e) => setEditCriticalPath(e.target.checked)}
            className="rounded border-[var(--border-color)] bg-[var(--bg-primary)]"
          />
          <span>🔴 Critical Path</span>
        </label>
      </div>

      {/* Save / Cancel */}
      <div className="flex items-center gap-2">
        <button
          onClick={handleSave}
          disabled={!editDescription.trim() || isSaving}
          className="flex items-center gap-1 px-3 py-1 bg-primary text-white rounded text-xs font-medium hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <Check className="h-3.5 w-3.5" />
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          className="flex items-center gap-1 px-3 py-1 bg-[var(--bg-primary)] text-[var(--text-secondary)] rounded text-xs font-medium hover:bg-[var(--bg-secondary)] border border-[var(--border-color)] transition-colors"
        >
          <X className="h-3.5 w-3.5" />
          Cancel
        </button>
      </div>
    </div>
  )
}
